import JobSearchViewModel, {
  TicketsAdapter,
} from '../../../infra/JobSearchViewModel'
import JobSearchAdapter from '../../../infra/JobSearchAdapter'
import { JobTicketStore } from '../../../tickets/JobTicketStore'
import { AlertFactory } from '../../../../alerts/Alert'
import { FilterTeamMembers } from '../../../../iam/TeamMemberStore'
import { FindLegends } from '../../../../legends/PublishedLegendStore'
import { GetCurrentUserWorkspaceMemberships } from '../../../../iam/WorkspaceMembershipStore'
import { GetCurrentMember, GetCurrentUser } from '../../../../iam/SessionStore'
import UserDataStore from '../../../../iam/UserDataStore'
import { FilterStorageFactory } from '../../../../infra/FiltersViewModel'
import { FindUserTeams } from '../../../../iam/TeamStore'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import identity from 'lodash/identity'
import { JobsLoadFilterItemsAdapter } from '../../../infra/JobsLoadFilterItemsAdapter'
import { task } from 'mobx-task'
import { SelectFilter } from '../../../../infra/FilterControls/Select/SelectFilter'
import { JobProgressionStepFilterOption } from '../../../views/JobManagement/JobProgressionFilterOption'
import { JobProgressionStepStatus } from '../../../progression/JobProgression'
import FirmDashboardStore from '../FirmDashboardStore'
import { JobsTotalPricing } from '../dtos/JobsTotalPricing'
import { Subscription } from 'rxjs'
import { Id } from '../../../../types/definitions'
import { JobScopeOption } from '../../../dtos/job'
import JobSearchQueryStringBuilder, {
  isNotJobStatusClosed,
} from '../../../infra/JobSearchQueryStringBuilder'
import { map, distinctUntilChanged, filter } from 'rxjs/operators'
import Workspace from 'iam/models/Workspace'
import { TickEvery, clock } from '../../../../utils/rx-util'
import { computePhaseOfDay } from '../../../../utils/greeting-util'
import { fromStream } from '../../../../utils/mobx-util'
import { DateRange } from 'infra/FilterControls/DateRange/DateRangeFilter'
import FirmDashboardContractViewModel from '../FirmDashboardContractViewModel'
import { SearchInventoryItems } from 'admin/InventoryStore'

/**
 * View model for managing the firm dashboard overview sidebar.
 */
export default class FirmDashboardOverviewSidebarViewModel {
  /**
   * Emits the phase of day (Morning, Afternoon, Evening) in real-time.
   */
  private phaseOfDay$ = clock(TickEvery.Minute).pipe(
    map(computePhaseOfDay),
    distinctUntilChanged(),
  )

  /**
   * RxJS Observables as MobX observables.
   */
  private $ = {
    phaseOfDay: fromStream(computePhaseOfDay(new Date()), this.phaseOfDay$),
  }

  /**
   * Total sum for submitted and closed jobs pricing.
   */
  @observable
  jobsTotalPricing: JobsTotalPricing = {
    submittedJobsTotalPrice: 0,
    closedJobsTotalPrice: 0,
  }

  /**
   * Total count for jobs with `OPEN_ITEMS` progression.
   */
  @observable
  openItemsJobsCount: number = 0

  /**
   * Total count for jobs with `DRAFT_IN_REVIEW` progression.
   */
  @observable
  draftReviewJobsCount: number = 0

  /**
   * Adapter to load filter items.
   */
  loadFilterItemsAdapter = new JobsLoadFilterItemsAdapter(
    this.getCurrentUserWorkspaceMemberships,
    this.findLegends,
    this.findUserTeams,
    this.searchServiceItems,
  )

  /**
   * Adapter for retrieving tickets.
   */
  ticketsAdapter: TicketsAdapter = {
    fetchLatestOpenTicketsForJobs:
      this.jobTicketStore.fetchLatestOpenTicketsForJobs,
  }

  /**
   * Job Searcher.
   */
  searcher = new JobSearchViewModel(
    this.jobFetcher,
    this.loadFilterItemsAdapter,
    this.ticketsAdapter,
    this.newAlert,
    identity,
    this.getCurrentMember,
    this.userDataStore,
    this.getCurrentUserWorkspaceMemberships,
    this.filterTeamMembers,
    this.createFilterStorage('FirmDashboardOverviewSidebar'),
  )

  contractInfo = new FirmDashboardContractViewModel()

  /**
   * Observable subscription used for disposing on deactivate.
   */
  private subscription = new Subscription()

  /**
   * Constructor
   */
  constructor(
    private jobFetcher: JobSearchAdapter,
    private jobTicketStore: JobTicketStore,
    private newAlert: AlertFactory,
    private findLegends: FindLegends,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private getCurrentMember: GetCurrentMember,
    private userDataStore: UserDataStore,
    private createFilterStorage: FilterStorageFactory,
    private filterTeamMembers: FilterTeamMembers,
    private findUserTeams: FindUserTeams,
    private firmDashboardStore: FirmDashboardStore,
    private readonly getCurrentUser: GetCurrentUser,
    private searchServiceItems: SearchInventoryItems,
  ) {
    makeObservable(this)
  }

  /**
   * The current phase of day.
   */
  @computed
  get phaseOfDay() {
    return this.$.phaseOfDay()
  }

  /**
   * Filter by current user workspace memberships
   * and returns a workspace.
   */
  @computed
  get currentWorkspace() {
    return (
      this.getCurrentUserWorkspaceMemberships().find(
        (ws) => ws.workspace.id === this.workspaceFilter.value[0],
      )?.workspace ?? this.getCurrentMember().workspace
    )
  }

  /**
   * The given name of the current user logged in.
   */
  @computed
  get userName() {
    return this.getCurrentUser().givenName
  }

  @computed
  get canViewContractAmount() {
    return (
      this.contractInfo.isContractConfigEnabled &&
      this.getCurrentMember().hasPermission('VIEW_CONTRACT_AMOUNT')
    )
  }

  /**
   * Contract usage, total + all the fees
   */
  @computed
  get contractUsage() {
    return (
      this.jobsTotalPricing.submittedJobsTotalPrice +
      this.jobsTotalPricing.closedJobsTotalPrice +
      this.contractInfo.feeAmount
    )
  }

  /**
   * The list of jobs that comes through the searcher.
   */
  @computed
  get jobs() {
    return this.searcher.jobs
  }

  /**
   * Filtered jobs by `OPEN_ITEMS` progression.
   */
  @computed
  get openItemsJobs() {
    return this.jobs.filter(
      (job) =>
        job.progressionStep === JobProgressionStepStatus.OPEN_ITEMS &&
        job.status !== 'CLOSED',
    )
  }

  /**
   * Filtered jobs by `DRAFT_IN_REVIEW` progression.
   */
  @computed
  get draftInReviewJobs() {
    return this.jobs.filter(
      (job) =>
        job.progressionStep === JobProgressionStepStatus.DRAFT_IN_REVIEW &&
        job.status !== 'CLOSED',
    )
  }

  /**
   * Gets the workspace filter.
   */
  @computed
  get workspaceFilter() {
    return this.searcher.filters.getFilter('workspaces') as SelectFilter<
      string,
      Id
    >
  }

  /**
   * Sets the workspace filter
   */
  async setWorkspace(workspace: Workspace) {
    this.workspaceFilter.change([workspace.id])
    this.contractInfo.setCurrentWorkspace(workspace)
    this.searcher.setForcedCreatedDateRange({
      from: this.contractInfo.contractStartDate,
      to: this.contractInfo.contractEndDate,
    } as DateRange)
    this.fetchJobsTotal(workspace)
  }

  /**
   * 1. Fetch the total sum for submitted and closed jobs pricing.
   * 2. Fetch the total count for jobs with progression:
   * `OPEN_ITEMS`, `DRAFT_IN_REVIEW`
   * @param workspace
   */
  fetchJobsTotal = task(async (workspace: Workspace) => {
    const [jobsTotalPricing, openItemsCount, draftItemsCount] =
      await Promise.all([
        this.firmDashboardStore.getJobsTotalPricing({
          workspaceId: workspace.id,
          contractStartDate: this.contractInfo.contractStartDate,
          contractEndDate: this.contractInfo.contractEndDate,
        }),
        this.jobFetcher.getTotal({
          scope: JobScopeOption.All,
          filter: new JobSearchQueryStringBuilder()
            .withWorkspaceIds([workspace.id])
            .withProgressionSteps([JobProgressionStepStatus.OPEN_ITEMS])
            .withinDateCreatedRange({
              from: this.contractInfo.contractStartDate,
              to: this.contractInfo.contractEndDate,
            } as DateRange)
            .withStatuses(isNotJobStatusClosed)
            .build(),
          $limit: 0,
        }),
        this.jobFetcher.getTotal({
          scope: JobScopeOption.All,
          filter: new JobSearchQueryStringBuilder()
            .withWorkspaceIds([workspace.id])
            .withProgressionSteps([JobProgressionStepStatus.DRAFT_IN_REVIEW])
            .withinDateCreatedRange({
              from: this.contractInfo.contractStartDate,
              to: this.contractInfo.contractEndDate,
            } as DateRange)
            .withStatuses(isNotJobStatusClosed)
            .build(),
          $limit: 0,
        }),
      ])

    runInAction(() => {
      this.jobsTotalPricing = jobsTotalPricing
      this.openItemsJobsCount = openItemsCount
      this.draftReviewJobsCount = draftItemsCount
    })
  })

  /**
   * Initialize the filters.
   */
  private initializeFilter() {
    const workspaceFilter = this.workspaceFilter
    workspaceFilter.selected = [Id.assert(this.currentWorkspace.id)]

    this.searcher.filters.makeFilterImplicit('progressionStep')

    const progressionFilter = this.searcher.filters.getFilter(
      'progressionStep',
    ) as SelectFilter<string, JobProgressionStepFilterOption>

    progressionFilter.selected = [
      JobProgressionStepFilterOption.OpenItems,
      JobProgressionStepFilterOption.DraftInReview,
    ]
  }

  /**
   * Activates the view model.
   */
  activate = task.resolved(async () => {
    this.searcher.setForcedCreatedDateRange({
      from: this.contractInfo.contractStartDate,
      to: this.contractInfo.contractEndDate,
    } as DateRange)
    this.searcher.activate()
    this.initializeFilter()

    this.subscription = new Subscription()
    this.subscription = this.jobFetcher.events$
      .pipe(filter((event) => event.type === 'JobUpdated'))
      .subscribe(async (event) => {
        // we do this here, because the searcher dismisses any update
        // once a filter is applied
        if (event.job.workspace.id === this.currentWorkspace.id) {
          runInAction(() => this.searcher.scroller.items.unshift(event.job))
          await this.fetchJobsTotal(this.currentWorkspace)
        }
      })
  })

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.searcher.deactivate()
    this.subscription.unsubscribe()
  }
}
